import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ClusterSnapshot, GpuSeries, NodeState } from "../../types";

/* ================================================================== */
/*  Public types                                                       */
/* ================================================================== */

interface Props {
  snapshot: ClusterSnapshot;
  onDrillNode: (name: string) => void;
}

export interface TopologyHandle {
  openReferencePanel: () => void;
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const last = (arr: number[]): number => (arr.length ? arr[arr.length - 1] : 0);
const fmt = (n: number, d = 0) => Number(n).toFixed(d);

type Status = "ok" | "warn" | "crit";

function statusOf(nodes: NodeState[], gpus: GpuSeries[]): Status {
  if (nodes.some((n) => n.state === "down") || gpus.some((g) => g.status === "down"))
    return "crit";
  if (gpus.some((g) => g.status === "xid-faulted" || g.xid_count > 0))
    return "warn";
  return "ok";
}

function badgeOf(nd: NodeState | undefined, gpus: GpuSeries[]) {
  if (!nd || nd.state === "down") return { label: "DOWN", warn: true };
  if (gpus.some((g) => g.status === "xid-faulted")) return { label: "FAULT", warn: true };
  if (nd.state === "alloc" || nd.state === "mix") return { label: "ALLO", warn: false };
  return { label: "IDLE", warn: false };
}

const STATUS_COLORS: Record<Status, string> = {
  ok: "var(--emerald)",
  warn: "var(--amber)",
  crit: "var(--rose)",
};

/* ================================================================== */
/*  Tooltip system (context-based, matches design)                     */
/* ================================================================== */

interface TipApi {
  show: (content: React.ReactNode, e: React.MouseEvent) => void;
  move: (e: React.MouseEvent) => void;
  hide: () => void;
}

const TipCtx = createContext<TipApi | null>(null);

function TipProvider({
  children,
  disabled,
}: {
  children: React.ReactNode;
  disabled: boolean;
}) {
  const [tip, setTip] = useState<{
    content: React.ReactNode;
    x: number;
    y: number;
    opacity: number;
  } | null>(null);
  const timer = useRef<number>(0);

  const show = useCallback(
    (content: React.ReactNode, e: React.MouseEvent) => {
      if (disabled) return;
      clearTimeout(timer.current);
      const x = e.clientX,
        y = e.clientY;
      timer.current = window.setTimeout(
        () => setTip({ content, x, y, opacity: 0 }),
        150,
      );
    },
    [disabled],
  );

  const move = useCallback((e: React.MouseEvent) => {
    setTip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t));
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setTip(null);
  }, []);

  useEffect(() => {
    if (disabled) hide();
  }, [disabled, hide]);

  // fade in after mount
  useEffect(() => {
    if (!tip || tip.opacity === 1) return;
    requestAnimationFrame(() =>
      setTip((t) => (t ? { ...t, opacity: 1 } : t)),
    );
  }, [tip?.content]);

  // position with edge flipping
  const style = useMemo(() => {
    if (!tip) return null;
    const W = 280,
      H = 120,
      pad = 12;
    let left = tip.x + 14,
      top = tip.y + 14;
    if (left + W > window.innerWidth - pad) left = tip.x - W - 14;
    if (top + H > window.innerHeight - pad) top = tip.y - H - 14;
    if (left < pad) left = pad;
    if (top < pad) top = pad;
    return {
      left,
      top,
      opacity: tip.opacity,
      transition: "opacity 80ms ease-out",
    } as const;
  }, [tip]);

  return (
    <TipCtx.Provider value={{ show, move, hide }}>
      {children}
      {tip && (
        <div
          style={{
            position: "fixed",
            zIndex: 100,
            pointerEvents: "none",
            borderRadius: 6,
            border: "1px solid rgba(78,225,240,0.3)",
            background: "rgba(7,9,13,0.95)",
            backdropFilter: "blur(8px)",
            padding: "8px 12px",
            fontSize: 12,
            fontFamily: "var(--mono)",
            color: "var(--fg-1)",
            maxWidth: 320,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            lineHeight: 1.55,
            ...style!,
          }}
        >
          {tip.content}
        </div>
      )}
    </TipCtx.Provider>
  );
}

function useHover(content: React.ReactNode) {
  const ctx = useContext(TipCtx);
  if (!ctx)
    return {} as {
      onMouseEnter?: undefined;
      onMouseMove?: undefined;
      onMouseLeave?: undefined;
    };
  return {
    onMouseEnter: (e: React.MouseEvent) => ctx.show(content, e),
    onMouseMove: (e: React.MouseEvent) => ctx.move(e),
    onMouseLeave: () => ctx.hide(),
  };
}

/* ================================================================== */
/*  Tiny reusable sub-components                                       */
/* ================================================================== */

function StatusDot({ status, size = 7 }: { status: Status; size?: number }) {
  const color = STATUS_COLORS[status];
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: color,
          opacity: 0.6,
          animation: "pulse 2s ease-in-out infinite",
        }}
      />
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: color,
          boxShadow: `0 0 6px ${color}`,
        }}
      />
    </span>
  );
}

function Bar({
  frac,
  color,
  glow,
  height = 4,
}: {
  frac: number;
  color: string;
  glow?: boolean;
  height?: number;
}) {
  return (
    <div
      style={{
        height,
        background: "var(--bg-4)",
        borderRadius: 3,
        overflow: "hidden",
        flex: 1,
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${(Math.max(0, Math.min(1, frac)) * 100).toFixed(1)}%`,
          background: color,
          borderRadius: 3,
          transition: "width 700ms ease-out",
          boxShadow: glow ? `0 0 6px ${color}` : undefined,
        }}
      />
    </div>
  );
}

/* ================================================================== */
/*  Side-panel content                                                 */
/* ================================================================== */

type PanelKind = "tray-detail" | "reference";
interface PanelState {
  kind: PanelKind;
  trayName?: string;
}

function PanelContent({
  panel,
  snapshot,
  onDrillNode,
  onClose,
}: {
  panel: PanelState;
  snapshot: ClusterSnapshot;
  onDrillNode: (n: string) => void;
  onClose: () => void;
}) {
  const hdr = (title: string) => (
    <div
      style={{
        padding: "12px 20px",
        borderBottom: "1px solid var(--line)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: "var(--bg-2)",
      }}
    >
      <span
        style={{
          width: 3,
          height: 28,
          borderRadius: 2,
          background: "var(--cyan)",
          boxShadow: "0 0 8px var(--cyan)",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          flex: 1,
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </span>
      <button
        onClick={onClose}
        className="btn xs ghost"
        style={{ fontSize: 14 }}
      >
        ✕
      </button>
    </div>
  );

  if (panel.kind === "reference") {
    return (
      <>
        {hdr("Full NVL72 Rack Reference")}
        <div
          style={{
            padding: 20,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            fontSize: 11,
          }}
        >
          {[
            {
              title: "COMPUTE",
              rows: [
                ["Vera CPUs", "36"],
                ["Rubin GPU pkgs", "72"],
                ["GPU dies", "144"],
                ["NVFP4 inference", "3.6 EFLOPS"],
                ["Training", "2.5 EFLOPS"],
              ],
            },
            {
              title: "MEMORY",
              rows: [
                ["HBM4 (GPU)", "20.7 TB"],
                ["HBM4 bandwidth", "3.17 PB/s"],
                ["LPDDR5X (CPU)", "54 TB"],
              ],
            },
            {
              title: "SCALE-UP",
              rows: [
                ["NVLink 6 per GPU", "3.6 TB/s"],
                ["NVLink ASICs", "36 (9×4)"],
                ["Aggregate BW", "260 TB/s"],
                ["In-network FP8", "130 TFLOPS"],
              ],
            },
            {
              title: "SCALE-OUT",
              rows: [
                ["CX-9 per Superchip", "1.6 Tb/s"],
                ["BlueField-4 DPUs", "18"],
                ["QX800 switches", "2+ (spine)"],
              ],
            },
          ].map((sec) => (
            <div
              key={sec.title}
              style={{
                background: "rgba(18,24,33,0.6)",
                border: "1px solid var(--line-2)",
                borderRadius: 4,
                padding: 12,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.15em",
                  color: "var(--cyan)",
                  marginBottom: 6,
                }}
              >
                {sec.title}
              </div>
              {sec.rows.map(([l, v]) => (
                <div
                  key={l}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "2px 0",
                    borderBottom: "1px dotted var(--line)",
                  }}
                >
                  <span style={{ color: "var(--fg-3)" }}>{l}</span>
                  <span style={{ color: "var(--fg-0)" }}>{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </>
    );
  }

  // tray-detail
  const name = panel.trayName!;
  const nd = snapshot.nodes.find((n) => n.name === name);
  const trayGpus = snapshot.gpus.filter((g) => g.node === name);

  return (
    <>
      {hdr(name)}
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, flex: 1, overflow: "auto" }}>
        {/* stat grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {[
            { l: "CPU UTIL", v: `${Math.round(last(nd?.cpu_util ?? [0]))}%`, accent: true },
            ...trayGpus.slice(0, 2).map((g, i) => ({
              l: `PKG-${i} UTIL`,
              v: `${Math.round(last(g.util))}%`,
              accent: g.status !== "xid-faulted",
            })),
          ].map((s) => (
            <div
              key={s.l}
              style={{
                borderRadius: 4,
                border: "1px solid var(--line-2)",
                background: "rgba(18,24,33,0.6)",
                padding: 8,
              }}
            >
              <div style={{ fontSize: 9, color: "var(--fg-3)", letterSpacing: "0.1em" }}>
                {s.l}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  color: s.accent ? "var(--cyan)" : "var(--rose)",
                }}
              >
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* hardware spec */}
        <div>
          <div style={{ fontSize: 10, color: "var(--cyan)", letterSpacing: "0.15em", fontWeight: 600, marginBottom: 6 }}>
            HARDWARE SPEC
          </div>
          {[
            ["Vera CPU", "88C / 176T · Olympus v9"],
            ["CPU memory", "LPDDR5X 1.5 TB @ 1.2 TB/s"],
            ["NVLink-C2C", "1.8 TB/s coherent"],
            ...trayGpus.slice(0, 2).map((g, i) => [
              `Rubin pkg-${i}`,
              `${Math.round(g.mem_total_mb / 1024)} GB HBM4 · 22 TB/s`,
            ]),
            ["ConnectX-9", "1.6 Tb/s scale-out"],
          ].map(([l, v]) => (
            <div
              key={l}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "3px 0",
                borderBottom: "1px dotted var(--line)",
                fontSize: 11,
              }}
            >
              <span style={{ color: "var(--fg-3)" }}>{l}</span>
              <span style={{ color: "var(--fg-0)" }}>{v}</span>
            </div>
          ))}
        </div>

        {/* recent events */}
        <div>
          <div style={{ fontSize: 10, color: "var(--cyan)", letterSpacing: "0.15em", fontWeight: 600, marginBottom: 6 }}>
            RECENT EVENTS
          </div>
          {trayGpus
            .filter((g) => g.xid_count > 0)
            .map((g) => (
              <div
                key={g.id}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "3px 0",
                  borderBottom: "1px solid var(--line)",
                  fontSize: 11,
                }}
              >
                <span style={{ color: "var(--fg-3)", width: 40, flexShrink: 0 }}>now</span>
                <span style={{ color: "var(--rose)" }}>
                  XID {g.last_xid_code} on pkg-{g.gpu_id} · {g.xid_count} total
                </span>
              </div>
            ))}
          {trayGpus.every((g) => g.xid_count === 0) && (
            <div style={{ color: "var(--fg-3)", fontSize: 11 }}>No recent events</div>
          )}
        </div>

        {/* chart placeholder */}
        <div
          className="placeholder-stripe"
          style={{
            height: 80,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--fg-3)",
            fontSize: 10,
          }}
        >
          15-min metrics chart (coming soon)
        </div>

        <button
          className="btn primary"
          onClick={() => onDrillNode(name)}
          style={{ width: "100%", justifyContent: "center", padding: "10px 0" }}
        >
          Open in node drill-down →
        </button>
      </div>
    </>
  );
}

/* ================================================================== */
/*  Connector lines SVG (animated traffic pulses)                      */
/* ================================================================== */

const ConnectorLines = memo(function ConnectorLines() {
  const W = 1000,
    H = 100;
  const sx1 = W * 0.25,
    sx2 = W * 0.75;
  const ry1 = W * 0.25,
    ry2 = W * 0.75;
  const sy = 30,
    ey = H;

  const paths = [
    `M ${sx1} ${sy} L ${ry1} ${ey}`,
    `M ${sx1} ${sy} L ${ry2} ${ey}`,
    `M ${sx2} ${sy} L ${ry1} ${ey}`,
    `M ${sx2} ${sy} L ${ry2} ${ey}`,
  ];

  return (
    <svg
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 60,
        width: "100%",
        pointerEvents: "none",
      }}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="connFade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="var(--blue)" stopOpacity={0.5} />
          <stop offset="1" stopColor="var(--blue)" stopOpacity={0.15} />
        </linearGradient>
      </defs>
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke="url(#connFade)"
          strokeWidth={1.5}
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {paths.map((d, i) => (
        <circle
          key={`p${i}`}
          r="3"
          fill="var(--blue)"
          opacity={0.9}
          style={{ filter: "drop-shadow(0 0 4px var(--blue))" }}
        >
          <animateMotion
            dur={`${2.4 + i * 0.7}s`}
            repeatCount="indefinite"
            begin={`${i * 0.6}s`}
            path={d}
          />
          <animate
            attributeName="opacity"
            values="0;0.95;0.95;0"
            keyTimes="0;0.15;0.85;1"
            dur={`${2.4 + i * 0.7}s`}
            repeatCount="indefinite"
            begin={`${i * 0.6}s`}
          />
        </circle>
      ))}
    </svg>
  );
});

/* ================================================================== */
/*  Sub-components: SwitchTile, RubinBlock, BF4Pill, NVLinkStrip, Legend */
/* ================================================================== */

function SwitchTile({
  name,
  active,
  errs,
  status,
}: {
  name: string;
  active: number;
  errs: number;
  status: Status;
}) {
  const hover = useHover(
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontWeight: 600, color: "var(--cyan)", letterSpacing: "0.04em" }}>
        QUANTUM-X800 · {name}
      </div>
      <div style={{ color: "var(--fg-1)" }}>XDR InfiniBand · 800 Gb/s/port</div>
      <div style={{ color: "var(--fg-1)" }}>up to 144 ports · OSFP cages</div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
        {["SHARPv4", "Adaptive Routing", "Telemetry CC"].map((p) => (
          <span
            key={p}
            style={{
              fontSize: 9,
              color: "var(--cyan)",
              border: "1px solid rgba(78,225,240,0.4)",
              borderRadius: 3,
              padding: "2px 6px",
            }}
          >
            {p}
          </span>
        ))}
      </div>
    </div>,
  );

  return (
    <div
      {...hover}
      style={{
        background: "rgba(7,9,13,0.4)",
        border: "1px solid var(--line-2)",
        borderRadius: 6,
        padding: "10px 12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <StatusDot status={status} size={6} />
        <span style={{ fontSize: 11, color: "var(--fg-0)", letterSpacing: "0.04em" }}>
          {name}
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 9,
            color: "var(--fg-3)",
            letterSpacing: "0.06em",
          }}
        >
          scale-out
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span
          style={{
            fontSize: 16,
            fontVariantNumeric: "tabular-nums",
            color: "var(--blue)",
            fontWeight: 600,
          }}
        >
          {active}
        </span>
        <span style={{ fontSize: 10, color: "var(--fg-3)" }}>/ 144 active</span>
      </div>
      <div style={{ fontSize: 10, color: "var(--fg-3)", marginTop: 2 }}>
        <span style={{ color: errs > 5 ? "var(--amber)" : "var(--fg-2)" }}>
          {errs}
        </span>{" "}
        sym errs / 24h
      </div>
    </div>
  );
}

function RubinBlock({
  g,
  idx,
}: {
  g: GpuSeries;
  idx: number;
}) {
  const u = last(g.util);
  const xid = g.status === "xid-faulted";
  const down = g.status === "down";
  const memFrac = g.mem_total_mb ? last(g.mem_mb) / g.mem_total_mb : 0;

  const hover = useHover(
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontWeight: 600, color: "var(--cyan)", letterSpacing: "0.04em" }}>
        RUBIN GPU PACKAGE
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        2 dies · {Math.round(g.mem_total_mb / 1024)} GB HBM4 · 22 TB/s
      </div>
      <div style={{ color: "var(--fg-1)" }}>50 PFLOPS NVFP4</div>
      <div style={{ color: "var(--fg-2)", fontSize: 10 }}>
        util {fmt(u)}% · {fmt(last(g.power_w))}W · {fmt(last(g.temp_c))}°C
      </div>
      {xid && (
        <div style={{ color: "var(--rose)", fontSize: 10, marginTop: 2 }}>
          XID fault — see drill-down
        </div>
      )}
    </div>,
  );

  const hbmHover = useHover(
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontWeight: 600, color: "var(--violet)", letterSpacing: "0.04em" }}>
        HBM4 MEMORY
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        {Math.round(g.mem_total_mb / 1024)} GB · 22 TB/s
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        current usage: {fmt(memFrac * 100)}%
      </div>
    </div>,
  );

  return (
    <div
      {...hover}
      style={{
        borderRadius: 4,
        padding: "4px 8px",
        border: `1px solid ${down ? "var(--line-2)" : xid ? "rgba(255,95,122,0.4)" : "var(--line-2)"}`,
        background: down
          ? "rgba(18,24,33,0.3)"
          : xid
          ? "rgba(255,95,122,0.1)"
          : "rgba(7,9,13,0.4)",
        opacity: down ? 0.5 : 1,
        backgroundImage: down
          ? "repeating-linear-gradient(135deg, transparent 0 6px, rgba(148,163,184,0.06) 6px 7px)"
          : undefined,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 9, color: "var(--fg-2)", letterSpacing: "0.06em" }}>
          RUBIN pkg-{idx}
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 13,
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
            color: down ? "var(--fg-3)" : xid ? "var(--rose)" : "var(--cyan)",
          }}
        >
          {down ? "—" : `${fmt(u)}%`}
        </span>
      </div>
      <div {...hbmHover}>
        <Bar
          frac={down ? 0 : memFrac}
          color={down ? "var(--fg-3)" : xid ? "var(--rose)" : "var(--violet)"}
          glow={!down && !xid}
          height={4}
        />
      </div>
    </div>
  );
}

function BF4Pill() {
  const hover = useHover(
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontWeight: 600, color: "var(--blue)", letterSpacing: "0.04em" }}>
        BLUEFIELD-4 DPU
      </div>
      <div style={{ color: "var(--fg-1)" }}>64-core Grace + integrated CX-9</div>
      <div style={{ color: "var(--fg-1)" }}>800 Gb/s · infrastructure offload</div>
      <div style={{ color: "var(--fg-2)", fontSize: 10 }}>
        shared 1 per 2 Superchips
      </div>
    </div>,
  );
  return (
    <div
      {...hover}
      style={{
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        top: 12,
        zIndex: 10,
        background: "var(--bg-0)",
        border: "1px solid var(--line-3)",
        borderRadius: 20,
        padding: "3px 10px",
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 9,
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
      }}
    >
      <span style={{ color: "var(--blue)", fontWeight: 600, letterSpacing: "0.06em" }}>
        BF-4
      </span>
      <span style={{ color: "var(--fg-1)", fontVariantNumeric: "tabular-nums" }}>
        shared
      </span>
    </div>
  );
}

function NVLinkStripComp() {
  const hover = useHover(
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontWeight: 600, color: "var(--cyan)", letterSpacing: "0.04em" }}>
        NVLINK 6 SWITCH TRAY
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        4 ASICs · 28.8 TB/s · 14.4 TFLOPS FP8
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        in-network reductions · SHARPv4
      </div>
      <div style={{ color: "var(--fg-3)", fontSize: 10, marginTop: 2 }}>
        Full NVL72: 9 trays = 36 ASICs = 260 TB/s
      </div>
    </div>,
  );
  return (
    <div
      {...hover}
      style={{
        margin: "0 12px 8px",
        padding: "0 12px",
        height: 36,
        borderRadius: 4,
        border: "1px solid rgba(78,225,240,0.25)",
        background: "rgba(78,225,240,0.05)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 10,
      }}
    >
      <StatusDot status="ok" size={6} />
      <span style={{ color: "var(--fg-1)" }}>
        NVLink 6 SW ·{" "}
        <span style={{ color: "var(--cyan)" }}>28.8 TB/s</span>
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ color: "var(--emerald)", fontSize: 12 }}>✓</span>
    </div>
  );
}

function LegendStrip() {
  const hover = useHover(
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontWeight: 600, color: "var(--cyan)", letterSpacing: "0.04em" }}>
        FABRIC LEGEND
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        <span style={{ color: "var(--cyan)" }}>━━ scale-up</span> · NVLink 6 ·
        3.6 TB/s/link
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        <span style={{ color: "var(--blue)" }}>━━ scale-out</span> · XDR IB ·
        800 Gb/s
      </div>
      <div style={{ color: "var(--fg-1)" }}>
        <span style={{ color: "var(--violet)" }}>┄┄ superchip</span> ·
        NVLink-C2C 1.8 TB/s coherent
      </div>
    </div>,
  );
  return (
    <div
      {...hover}
      style={{
        padding: "0 16px",
        height: 28,
        borderTop: "1px solid var(--line-2)",
        display: "flex",
        alignItems: "center",
        gap: 20,
        fontSize: 10,
        color: "var(--fg-3)",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 20,
            height: 2,
            background: "var(--cyan)",
            borderRadius: 1,
            display: "inline-block",
          }}
        />
        scale-up
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 20,
            height: 2,
            background: "var(--blue)",
            borderRadius: 1,
            display: "inline-block",
          }}
        />
        scale-out
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 20,
            height: 0,
            borderTop: "1px dashed var(--violet)",
            display: "inline-block",
          }}
        />
        superchip
      </span>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 9, opacity: 0.7 }}>hover for details</span>
    </div>
  );
}

/* ================================================================== */
/*  Main Topology component                                            */
/* ================================================================== */

export const Topology = forwardRef<TopologyHandle, Props>(function Topology(
  { snapshot, onDrillNode },
  ref,
) {
  const [panel, setPanel] = useState<PanelState | null>(null);

  useImperativeHandle(ref, () => ({
    openReferencePanel() {
      setPanel({ kind: "reference" });
    },
  }));

  // ESC to close panel
  useEffect(() => {
    if (!panel) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPanel(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [panel]);

  // Derived data
  const nodeNames = snapshot.nodes.map((n) => n.name);
  const rackSplit = Math.ceil(nodeNames.length / 2);
  const racks = [
    { name: "RACK-A", trays: nodeNames.slice(0, rackSplit) },
    { name: "RACK-B", trays: nodeNames.slice(rackSplit) },
  ];

  // Switch port aggregation
  const switchData = [0, 1].map((si) => {
    const ports = snapshot.ib_ports.filter((_, idx) => idx % 2 === si);
    const active = ports.filter((p) => p.state === 1).length;
    const errs = ports.reduce((a, p) => a + last(p.symbol_errors), 0);
    const allDown = ports.length > 0 && active === 0;
    const s: Status = allDown ? "crit" : errs > 5 ? "warn" : "ok";
    return { name: `QX800 · SW-${si + 1}`, active, errs, status: s };
  });

  return (
    <TipProvider disabled={!!panel}>
      <div
        style={{
          position: "relative",
          fontFamily: "var(--mono)",
          fontFeatureSettings: '"tnum"',
          color: "var(--fg-1)",
          fontSize: 11,
          background: "rgba(18,24,33,0.6)",
          border: "1px solid var(--line-2)",
          borderRadius: 8,
          display: "flex",
          flexDirection: "column",
          height: 620,
          overflow: "hidden",
        }}
      >
        {/* ======== HEADER ======== */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 16px",
            borderBottom: "1px solid var(--line-2)",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em" }}>
              Cluster Topology · NVL72 (partial)
            </div>
            <div style={{ fontSize: 10, color: "var(--fg-3)", marginTop: 2 }}>
              2 racks · 2 Superchips · 4 Rubin pkgs · 8 dies · 2× Quantum-X800
            </div>
          </div>
          <span
            className="chip ok"
            style={{ gap: 4, height: 20, padding: "0 8px", fontSize: 10 }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--emerald)",
                position: "relative",
                display: "inline-flex",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: "var(--emerald)",
                  animation: "pulse 1.4s ease-in-out infinite",
                }}
              />
            </span>
            LIVE
          </span>
          <button
            onClick={() => setPanel({ kind: "reference" })}
            style={{
              padding: "4px 10px",
              borderRadius: 4,
              fontSize: 10,
              color: "var(--cyan)",
              border: "1px solid rgba(78,225,240,0.3)",
              background: "rgba(78,225,240,0.05)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            ⓘ Full rack reference
          </button>
          <button
            title="Expand"
            onClick={() => {}}
            style={{
              padding: 6,
              borderRadius: 4,
              color: "var(--fg-2)",
              border: "1px solid var(--line-2)",
              background: "transparent",
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            ⛶
          </button>
        </div>

        {/* ======== SWITCHES ROW ======== */}
        <div
          style={{
            position: "relative",
            padding: "12px 16px",
            paddingBottom: 26,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              position: "relative",
              zIndex: 1,
            }}
          >
            {switchData.map((sw) => (
              <SwitchTile key={sw.name} {...sw} />
            ))}
          </div>
          <ConnectorLines />
        </div>

        {/* ======== RACKS ROW ======== */}
        <div
          style={{
            flex: 1,
            padding: "0 16px 12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            minHeight: 0,
          }}
        >
          {racks.map((r) => {
            const rackNodes = snapshot.nodes.filter((n) =>
              r.trays.includes(n.name),
            );
            const rackGpus = snapshot.gpus.filter((g) =>
              r.trays.includes(g.node),
            );
            const aggUtil =
              rackGpus.length > 0
                ? rackGpus.reduce((a, g) => a + last(g.util), 0) /
                  rackGpus.length
                : 0;
            const rStatus = statusOf(rackNodes, rackGpus);

            const rackHeaderHover = useHover(
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontWeight: 600, color: "var(--cyan)", letterSpacing: "0.04em" }}>
                  {r.name} · NVL72 SLICE
                </div>
                <div style={{ color: "var(--fg-1)" }}>
                  1.15 TB HBM4 shown · ~200 PFLOPS NVFP4
                </div>
                <div style={{ color: "var(--fg-1)" }}>
                  88 TB/s aggregate HBM bandwidth
                </div>
                <div style={{ color: "var(--fg-3)", fontSize: 10, marginTop: 2 }}>
                  Showing 2 of 36 Superchips
                </div>
              </div>,
            );

            return (
              <div
                key={r.name}
                style={{
                  background: "rgba(7,9,13,0.4)",
                  border: "1px solid var(--line-2)",
                  borderRadius: 6,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* Rack header */}
                <div
                  {...rackHeaderHover}
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid var(--line-2)",
                    background: "rgba(18,24,33,0.4)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <StatusDot status={rStatus} size={7} />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                    }}
                  >
                    {r.name}
                  </span>
                  <span
                    style={{
                      padding: "2px 6px",
                      borderRadius: 3,
                      fontSize: 9,
                      color: "var(--cyan)",
                      border: "1px solid rgba(78,225,240,0.4)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    NVL72
                  </span>
                  <span style={{ flex: 1 }} />
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--cyan)",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 600,
                    }}
                  >
                    {fmt(aggUtil)}%
                  </span>
                </div>

                {/* Aggregate util bar */}
                <div style={{ padding: "8px 12px 0" }}>
                  <Bar
                    frac={aggUtil / 100}
                    color="var(--cyan)"
                    glow
                    height={4}
                  />
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--fg-3)",
                      marginTop: 4,
                    }}
                  >
                    2 of 36 Superchips shown
                  </div>
                </div>

                {/* Trays */}
                <div
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                    position: "relative",
                    minHeight: 0,
                  }}
                >
                  {r.trays.map((tName, ti) => {
                    const nd = snapshot.nodes.find(
                      (n) => n.name === tName,
                    );
                    const trayGpus = snapshot.gpus.filter(
                      (g) => g.node === tName,
                    );
                    const badge = badgeOf(nd, trayGpus);
                    const tStatus = statusOf(nd ? [nd] : [], trayGpus);
                    const down = nd?.state === "down";
                    const fault = trayGpus.some(
                      (g) => g.status === "xid-faulted",
                    );
                    const cpuU = last(nd?.cpu_util ?? [0]);

                    const superchipHover = useHover(
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ fontWeight: 600, color: "var(--violet)", letterSpacing: "0.04em" }}>
                          VERA RUBIN SUPERCHIP
                        </div>
                        <div style={{ color: "var(--fg-1)" }}>
                          1 Vera CPU + 2 Rubin packages
                        </div>
                        <div style={{ color: "var(--fg-1)" }}>
                          NVLink-C2C · 1.8 TB/s coherent
                        </div>
                      </div>,
                    );

                    const veraHover = useHover(
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ fontWeight: 600, color: "var(--cyan)", letterSpacing: "0.04em" }}>
                          VERA CPU
                        </div>
                        <div style={{ color: "var(--fg-1)" }}>
                          88C / 176T · Olympus v9
                        </div>
                        <div style={{ color: "var(--fg-1)" }}>
                          LPDDR5X · 1.5 TB @ 1.2 TB/s
                        </div>
                      </div>,
                    );

                    const cxHover = useHover(
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ fontWeight: 600, color: "var(--blue)", letterSpacing: "0.04em" }}>
                          CONNECTX-9 SUPERNIC
                        </div>
                        <div style={{ color: "var(--fg-1)" }}>
                          1.6 Tb/s scale-out · RDMA
                        </div>
                        <div style={{ color: "var(--fg-1)" }}>
                          GPUDirect · inline IPsec
                        </div>
                      </div>,
                    );

                    return (
                      <div
                        key={tName}
                        onClick={() => {
                          setPanel({ kind: "tray-detail", trayName: tName });
                        }}
                        style={{
                          background: "rgba(18,24,33,0.5)",
                          border: `1px solid ${down ? "rgba(255,95,122,0.6)" : fault ? "rgba(255,95,122,0.5)" : "var(--line-2)"}`,
                          borderRadius: 6,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          cursor: "pointer",
                          transition: "border-color 150ms",
                          boxShadow: down
                            ? "inset 0 0 32px rgba(244,63,94,0.08)"
                            : undefined,
                        }}
                        onMouseOver={(e) => {
                          if (!down && !fault)
                            (e.currentTarget as HTMLDivElement).style.borderColor =
                              "rgba(78,225,240,0.5)";
                        }}
                        onMouseOut={(e) => {
                          if (!down && !fault)
                            (e.currentTarget as HTMLDivElement).style.borderColor =
                              "var(--line-2)";
                        }}
                      >
                        {/* tray header */}
                        <div
                          style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid var(--line-2)",
                            background: "rgba(18,24,33,0.4)",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <StatusDot status={tStatus} size={6} />
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 500,
                            }}
                          >
                            tray-{String(ti + 1).padStart(2, "0")}
                          </span>
                          <span style={{ flex: 1 }} />
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              letterSpacing: "0.06em",
                              color:
                                badge.warn
                                  ? "var(--rose)"
                                  : badge.label === "ALLO"
                                  ? "var(--cyan)"
                                  : "var(--fg-3)",
                            }}
                          >
                            {badge.label}
                          </span>
                        </div>

                        {/* tray body */}
                        <div
                          style={{
                            padding: 8,
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                            flex: 1,
                          }}
                        >
                          {/* superchip dashed outline */}
                          <div
                            {...superchipHover}
                            style={{
                              border: "1px dashed rgba(157,123,255,0.5)",
                              borderRadius: 4,
                              padding: 6,
                              background: "rgba(157,123,255,0.05)",
                              display: "flex",
                              flexDirection: "column",
                              gap: 4,
                            }}
                          >
                            {/* Vera CPU */}
                            <div
                              {...veraHover}
                              style={{
                                background: "rgba(7,9,13,0.4)",
                                border: "1px solid var(--line-2)",
                                borderRadius: 4,
                                padding: "4px 8px",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 9,
                                  color: "var(--fg-1)",
                                  letterSpacing: "0.06em",
                                  fontWeight: 500,
                                }}
                              >
                                VERA
                              </span>
                              <Bar
                                frac={cpuU / 100}
                                color="var(--cyan)"
                                height={4}
                              />
                              <span
                                style={{
                                  fontSize: 9,
                                  color: "var(--cyan)",
                                  fontVariantNumeric: "tabular-nums",
                                  width: 28,
                                  textAlign: "right",
                                }}
                              >
                                {fmt(cpuU)}%
                              </span>
                            </div>

                            {/* Rubin packages */}
                            {trayGpus.slice(0, 2).map((g, pi) => (
                              <RubinBlock key={g.id} g={g} idx={pi} />
                            ))}
                          </div>

                          <span style={{ flex: 1 }} />

                          {/* CX-9 footer */}
                          <div
                            {...cxHover}
                            style={{
                              borderTop: "1px solid var(--line-2)",
                              margin: "-0 -8px -8px",
                              padding: "4px 10px",
                              background: "rgba(18,24,33,0.4)",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              fontSize: 9,
                              color: "var(--fg-3)",
                            }}
                          >
                            <span
                              style={{
                                color: "var(--blue)",
                                fontWeight: 500,
                              }}
                            >
                              CX-9
                            </span>
                            <span style={{ flex: 1 }} />
                            <span>1.6 Tb/s</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <BF4Pill />
                </div>

                <NVLinkStripComp />

                {/* Rack footer */}
                <div
                  style={{
                    padding: "6px 12px",
                    borderTop: "1px solid var(--line-2)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 9,
                    color: "var(--fg-3)",
                  }}
                >
                  <StatusDot status={rStatus} size={5} />
                  <span>
                    NVLink 6 · 3.6 TB/s ·{" "}
                    {rStatus === "crit"
                      ? "4 lanes down"
                      : rStatus === "warn"
                      ? "degraded"
                      : "healthy"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ======== LEGEND ======== */}
        <LegendStrip />

        {/* ======== SIDE PANEL ======== */}
        {panel && (
          <>
            <div
              className="fade-in"
              onClick={() => setPanel(null)}
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(7,9,13,0.4)",
                backdropFilter: "blur(2px)",
                zIndex: 50,
              }}
            />
            <div
              className="drawer"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: 420,
                background: "var(--bg-1)",
                borderLeft: "1px solid var(--line-2)",
                zIndex: 51,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <PanelContent
                panel={panel}
                snapshot={snapshot}
                onDrillNode={onDrillNode}
                onClose={() => setPanel(null)}
              />
            </div>
          </>
        )}
      </div>
    </TipProvider>
  );
});
